// combatpro/spell/spellEngine.js
//
// Combat PRO - Spell Engine (Stage 45A: pure spell metadata, lookup, parsing).
//
// Pure, UI-independent spell logic extracted from the spell runtime in
// battletracker.html (the read-only master spec). This module has no React, no
// DOM, no context, no state, no side effects, and performs no network / cloud /
// save calls. It receives primitives and plain objects (including the spell
// database, which is INJECTED by the caller) and returns plain data or strings.
//
// SPELLS_DB INJECTION: the legacy reads a module-global SPELLS_DB sourced from an
// external script (window.VI_SPELLS_DB, battletracker.html 3750 / 4062). To keep
// this module pure, the functions that need the database take it as a parameter
// (spellsDb); the caller supplies window.VI_SPELLS_DB. The database is NEVER
// recreated, embedded, or mutated here.
//
// Source mapping (battletracker.html):
//   - SPELL_COLORS              VI_SPELL_COLORS (4070-4080)
//   - getSchoolColor            vi_getSchoolColor (4082-4085)
//   - getSpellCardClass         vi_getSpellCardClass (4625-4636)
//   - CONDITION_KEYWORDS /
//     BUFF_SCHOOLS /
//     DEBUFF_DESCRIPTORS        VI_CONDITION_KEYWORDS / VI_BUFF_SCHOOLS /
//                               VI_DEBUFF_DESCRIPTORS (4088-4090)
//   - parseDuration             parseDuration (4094-4110)
//   - parseDamage               parseDamage (4112-4125)
//   - parseSave                 parseSave (4127-4135)
//   - parseAoE                  parseAoE (4137-4143)
//   - parseSpellType            parseSpellType (4145-4160)
//   - parseAppliesCondition     parseAppliesCondition (4162-4170)
//   - mapApiSpell               vi_mapApiSpell (4172-4213)
//   - buildSortedSpellOptions   vi_buildSortedSpellOptions (4217-4240); takes the
//                               injected spellsDb for string resolution
//   - getSpellData              vi_getSpellData (4279-4284); lowercase DB lookup,
//                               with an optional injected cache (the legacy
//                               VI_SPELL_CACHE is API-populated; that API layer is
//                               DEFERRED, so the cache is an optional parameter and
//                               defaults to none)
//
// DEFERRED / NOT here (owned by later stages): spell casting, execution,
// targeting, the safe wrapper, applyDamage (Damage Runtime), applyCondition
// (Effects), saves application, concentration, duration tracking, preview, AI
// recommendations, custom-spell CRUD, cloud / save / API, combat log. No optional
// chaining. No literal em dash (the "no-damage" sentinel is written as "\u2014").

// School colour table (border + translucent background per magic school).
export const SPELL_COLORS = {
  evocation:     { border: "#ef4444", bg: "rgba(239,68,68,0.06)" },
  necromancy:    { border: "#7c3aed", bg: "rgba(124,58,237,0.06)" },
  conjuration:   { border: "#06b6d4", bg: "rgba(6,182,212,0.06)" },
  abjuration:    { border: "#22c55e", bg: "rgba(34,197,94,0.06)" },
  transmutation: { border: "#f59e0b", bg: "rgba(245,158,11,0.06)" },
  illusion:      { border: "#ec4899", bg: "rgba(236,72,153,0.06)" },
  enchantment:   { border: "#a855f7", bg: "rgba(168,85,247,0.06)" },
  divination:    { border: "#3b82f6", bg: "rgba(59,130,246,0.06)" },
  default:       { border: "rgba(255,255,255,0.15)", bg: "rgba(255,255,255,0.02)" }
};

// Resolve a school name to its colour pair (default when unknown / missing).
export function getSchoolColor(school) {
  if (!school) { return SPELL_COLORS.default; }
  return SPELL_COLORS[(school).toLowerCase()] || SPELL_COLORS.default;
}

// Spell card CSS class from spellType, falling back to damage / saveType /
// concentration. "\u2014" is the legacy "no value" sentinel (an em dash) used in
// the spell database; the comparison is preserved exactly.
export function getSpellCardClass(spell) {
  if (!spell) { return ""; }
  if (spell.spellType === "damage") { return "vi-dmg"; }
  if (spell.spellType === "control") { return "vi-ctrl"; }
  if (spell.spellType === "buff") { return "vi-conc"; }
  if (spell.spellType === "debuff") { return "vi-ctrl"; }
  if (spell.damage && spell.damage !== "\u2014") { return "vi-dmg"; }
  if (spell.saveType && spell.saveType !== "\u2014") { return "vi-ctrl"; }
  if (spell.concentration) { return "vi-conc"; }
  return "";
}

// Classification vocabularies (used by the parsers below).
export const CONDITION_KEYWORDS = ["stunned", "paralyzed", "restrained", "blinded", "frightened", "charmed", "poisoned", "prone", "incapacitated", "petrified", "grappled", "deafened", "invisible", "unconscious"];
export const BUFF_SCHOOLS = ["abjuration", "transmutation", "divination"];
export const DEBUFF_DESCRIPTORS = ["disadvantage", "penalty", "reduce", "slow", "weaken", "frighten", "charm", "curse"];

// Parse a raw duration string into rounds + concentration flag.
export function parseDuration(apiSpell) {
  try {
    const raw = (apiSpell.duration || "").toLowerCase();
    if (raw.includes("instantaneous")) { return { durationRounds: 0, concentration: false }; }
    const concMatch = raw.includes("concentration");
    const minMatch = raw.match(/(\d+)\s*minute/);
    const hourMatch = raw.match(/(\d+)\s*hour/);
    const roundMatch = raw.match(/(\d+)\s*round/);
    let rounds = 0;
    if (roundMatch) { rounds = parseInt(roundMatch[1]); }
    else if (minMatch) { rounds = parseInt(minMatch[1]) * 10; }
    else if (hourMatch) { rounds = parseInt(hourMatch[1]) * 600; }
    else if (raw.includes("until dispelled") || raw.includes("permanent")) { rounds = 9999; }
    else { rounds = 1; }
    return { durationRounds: rounds, concentration: concMatch || (apiSpell.concentration === true) };
  } catch (e) { return { durationRounds: 0, concentration: false }; }
}

// Parse damage formula + damage type from the API damage object.
export function parseDamage(apiSpell) {
  try {
    const dmg = apiSpell.damage || null;
    if (!dmg) { return { damageFormula: null, damageType: null }; }
    const typeObj = dmg.damage_type || null;
    const damageType = typeObj ? typeObj.name.toLowerCase() : null;
    const slotLevels = dmg.damage_at_slot_level || {};
    const charLevels = dmg.damage_at_character_level || {};
    const formulaMap = Object.keys(slotLevels).length > 0 ? slotLevels : charLevels;
    const firstKey = Object.keys(formulaMap).sort()[0];
    const damageFormula = firstKey ? formulaMap[firstKey] : null;
    return { damageFormula, damageType };
  } catch (e) { return { damageFormula: null, damageType: null }; }
}

// Parse saving throw metadata (type, success effect, attack type).
export function parseSave(apiSpell) {
  try {
    const dc = apiSpell.dc || null;
    if (!dc) { return { saveType: null, saveEffect: null, attackType: null }; }
    const saveType = dc.dc_type ? dc.dc_type.name : null;
    const saveEffect = dc.dc_success ? dc.dc_success.toLowerCase() : "half";
    return { saveType, saveEffect, attackType: "save" };
  } catch (e) { return { saveType: null, saveEffect: null, attackType: null }; }
}

// Parse area of effect (type + size) or null.
export function parseAoE(apiSpell) {
  try {
    const aoe = apiSpell.area_of_effect || null;
    if (!aoe) { return null; }
    return { type: (aoe.type || "").toLowerCase(), size: aoe.size || 0 };
  } catch (e) { return null; }
}

// Classify a spell into damage / control / debuff / buff / utility. Receives the
// already-parsed duration/damage/save bundle (parsed) as in the legacy.
export function parseSpellType(apiSpell, parsed) {
  try {
    const desc = ((apiSpell.desc || []).join(" ")).toLowerCase();
    if (parsed.damageFormula) { return "damage"; }
    if (parsed.saveType) {
      const hasCondition = CONDITION_KEYWORDS.some(k => desc.includes(k));
      if (hasCondition) { return "control"; }
      return "debuff";
    }
    const hasDebuff = DEBUFF_DESCRIPTORS.some(k => desc.includes(k));
    if (hasDebuff) { return "debuff"; }
    const school = (apiSpell.school ? (apiSpell.school.name || "").toLowerCase() : "");
    if (BUFF_SCHOOLS.includes(school) || desc.includes("gain") || desc.includes("advantage") || desc.includes("add")) { return "buff"; }
    return "utility";
  } catch (e) { return "utility"; }
}

// First condition keyword found in the description, or null.
export function parseAppliesCondition(apiSpell) {
  try {
    const desc = ((apiSpell.desc || []).join(" ")).toLowerCase();
    for (let i = 0; i < CONDITION_KEYWORDS.length; i++) {
      if (desc.includes(CONDITION_KEYWORDS[i])) { return CONDITION_KEYWORDS[i]; }
    }
    return null;
  } catch (e) { return null; }
}

// Map a raw API spell object into the engine's legacy-compatible spell shape.
// Composes the parsers above. No mutation of the input.
export function mapApiSpell(apiSpell) {
  const durParsed = parseDuration(apiSpell);
  const dmgParsed = parseDamage(apiSpell);
  const saveParsed = parseSave(apiSpell);
  const aoe = parseAoE(apiSpell);
  const parsed = { ...durParsed, ...dmgParsed, ...saveParsed };
  const spellType = parseSpellType(apiSpell, parsed);
  const appliesCondition = parseAppliesCondition(apiSpell);
  const typeColorMap = { damage: "vi-dmg", control: "vi-ctrl", buff: "vi-conc", debuff: "vi-tag-save", utility: "" };
  return {
    name: apiSpell.name || "",
    school: apiSpell.school ? apiSpell.school.name : "",
    level: apiSpell.level || 0,
    duration: durParsed.durationRounds,
    duration_text: apiSpell.duration || "",
    casting_time: apiSpell.casting_time || "",
    range: apiSpell.range || "",
    concentration: durParsed.concentration,
    saveDC: saveParsed.saveType ? "Spell DC" : null,
    saveType: saveParsed.saveType,
    damage: dmgParsed.damageFormula ? (dmgParsed.damageFormula + (dmgParsed.damageType ? " " + dmgParsed.damageType : "")) : null,
    upcast: apiSpell.higher_level ? apiSpell.higher_level[0] || null : null,
    desc: (apiSpell.desc || []).join(" "),
    spellType,
    damageType: dmgParsed.damageType,
    damageFormula: dmgParsed.damageFormula,
    attackType: saveParsed.attackType || (dmgParsed.damageFormula ? "ranged" : null),
    saveEffect: saveParsed.saveEffect,
    durationRounds: durParsed.durationRounds,
    appliesCondition,
    removesCondition: null,
    areaOfEffect: aoe,
    components: apiSpell.components || [],
    material: apiSpell.material || null,
    ritual: apiSpell.ritual || false,
    higherLevel: apiSpell.higher_level ? apiSpell.higher_level[0] : null,
    visualTag: typeColorMap[spellType] || "",
    icon: spellType === "damage" ? "\uD83D\uDD25" : spellType === "control" ? "\uD83D\uDD2E" : spellType === "buff" ? "\u2728" : spellType === "debuff" ? "\u2620\uFE0F" : "\uD83D\uDCD6"
  };
}

// Build grouped + sorted <optgroup> option HTML for a spell dropdown. spellList
// entries may be strings (resolved against the injected spellsDb) or objects.
// Grouped by school, sorted by level then name. Returns an HTML string exactly as
// the legacy vi_buildSortedSpellOptions; how a React component consumes it (or
// re-renders equivalent JSX) is a display-wiring decision left to the panel.
export function buildSortedSpellOptions(spellList, spellsDb) {
  const db = spellsDb || [];
  const resolved = spellList.map(s => {
    if (typeof s === "string") { return db.find(sp => sp.name === s) || { name: s, level: null, school: null }; }
    return s;
  }).filter(Boolean);
  const groups = {};
  resolved.forEach(s => {
    const key = (s.school && s.school.trim()) ? s.school.trim() : "Other";
    if (!groups[key]) { groups[key] = []; }
    groups[key].push(s);
  });
  const sortedKeys = Object.keys(groups).sort((a, b) => a.localeCompare(b));
  sortedKeys.forEach(key => {
    groups[key].sort((a, b) => {
      const levelA = Number(a.level != null ? a.level : 999);
      const levelB = Number(b.level != null ? b.level : 999);
      if (levelA !== levelB) { return levelA - levelB; }
      return String(a.name || "").localeCompare(String(b.name || ""));
    });
  });
  return sortedKeys.map(key =>
    '<optgroup label="' + key + '">' + groups[key].map(s => '<option value="' + s.name + '">[Lv ' + (s.level != null ? s.level : "?") + "] " + s.name + "</option>").join("") + "</optgroup>"
  ).join("");
}

// Look up a spell by name against the injected database (case-insensitive),
// returning the spell object or null. An optional cache (the legacy
// API-populated VI_SPELL_CACHE, whose API layer is DEFERRED) is consulted first
// when supplied. Pure: no globals, no network.
export function getSpellData(spellName, spellsDb, cache) {
  if (!spellName) { return null; }
  if (cache) {
    const cached = cache[spellName.toLowerCase()];
    if (cached) { return cached; }
  }
  const db = spellsDb || [];
  return db.find(s => s.name.toLowerCase() === spellName.toLowerCase()) || null;
}
